import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Tag, Button, Space, Descriptions, Typography, Select, message, Input, Row, Col, Divider, Table,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'PENDING_PAYMENT', label: 'Zahlung ausstehend' },
  { value: 'PAID', label: 'Bezahlt' },
  { value: 'SHIPPED', label: 'Versendet' },
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'CANCELLED', label: 'Storniert' },
];

const PAYMENT_OPTIONS = [
  { value: 'PENDING', label: 'Ausstehend' },
  { value: 'PAID', label: 'Bezahlt' },
  { value: 'FAILED', label: 'Fehlgeschlagen' },
];

const STATUS_COLORS: Record<string, string> = {
  PENDING_PAYMENT: 'orange',
  PAID: 'blue',
  SHIPPED: 'cyan',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

const PAYMENT_COLORS: Record<string, string> = {
  PENDING: 'orange',
  PAID: 'green',
  FAILED: 'red',
};

interface Order {
  id: string;
  orderNumber: string;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  customerPhone: string | null;
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  status: string;
  paymentStatus: string;
  paymentMethod: string | null;
  notes: string | null;
  deliveryMethod?: 'SHIPPING' | 'PICKUP' | null;
  customerNote?: string | null;
  discountCode?: string | null;
  discountCents?: number;
  createdAt: string;
  items: any[];
  addresses: any[];
}

const fmt = (c: number) => `${(c / 100).toLocaleString('de-DE')} €`;

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!id) return;
    fetchOrder();
    // eslint-disable-next-line
  }, [id]);

  const fetchOrder = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/admin/orders/${id}`);
      setOrder(res.data.order);
      setNotes(res.data.order?.notes || '');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const update = async (patch: Partial<Order>) => {
    if (!id) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/admin/orders/${id}`, patch);
      message.success('Aktualisiert');
      fetchOrder();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !order) {
    return <div>Lade Bestellung…</div>;
  }

  const billing = order.addresses.find((a: any) => a.type === 'BILLING');
  const shipping = order.addresses.find((a: any) => a.type === 'SHIPPING');

  const itemColumns = [
    { title: 'Marke', dataIndex: 'watchBrand', key: 'watchBrand' },
    { title: 'Modell', dataIndex: 'watchModel', key: 'watchModel' },
    { title: 'Referenz', dataIndex: 'watchReferenceNumber', key: 'watchReferenceNumber' },
    {
      title: 'Preis',
      dataIndex: 'unitPriceCents',
      key: 'unitPriceCents',
      render: (c: number) => fmt(c),
      align: 'right' as const,
    },
    {
      title: 'Produkt',
      key: 'product-link',
      width: 130,
      render: (_: any, r: any) => (
        <Button
          size="small"
          type="link"
          onClick={() => navigate(`/products/${r.watchId}`)}
          data-testid={`order-goto-watch-${r.watchId}`}
        >
          Zum Produkt
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-order">
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')}>
          Zurück
        </Button>
        <Title level={3} style={{ margin: 0 }}>{order.orderNumber}</Title>
        <Tag color={STATUS_COLORS[order.status] || 'default'}>{order.status}</Tag>
        <Tag color={PAYMENT_COLORS[order.paymentStatus] || 'default'}>
          Zahlung: {order.paymentStatus}
        </Tag>
        <Tag color={order.deliveryMethod === 'PICKUP' ? 'gold' : 'blue'} data-testid="order-delivery-method-tag">
          {order.deliveryMethod === 'PICKUP' ? 'Abholung' : 'Versand'}
        </Tag>
      </Space>

      <Card style={{ marginBottom: 16 }} title="Status & Aktionen">
        <Space wrap size="large" direction={isMobile ? 'vertical' : 'horizontal'} style={isMobile ? { width: '100%' } : undefined}>
          <div>
            <Text type="secondary" style={{ marginRight: 8, display: isMobile ? 'block' : undefined }}>Bestellstatus</Text>
            <Select
              value={order.status}
              options={STATUS_OPTIONS}
              style={{ width: isMobile ? '100%' : 220 }}
              onChange={(v) => update({ status: v })}
              loading={saving}
              data-testid="order-status-select"
            />
          </div>
          <div>
            <Text type="secondary" style={{ marginRight: 8, display: isMobile ? 'block' : undefined }}>Zahlung</Text>
            <Select
              value={order.paymentStatus}
              options={PAYMENT_OPTIONS}
              style={{ width: isMobile ? '100%' : 200 }}
              onChange={(v) => {
                const patch: any = { paymentStatus: v };
                // when paid, also bump order status if still pending payment
                if (v === 'PAID' && order.status === 'PENDING_PAYMENT') {
                  patch.status = 'PAID';
                }
                update(patch);
              }}
              loading={saving}
              data-testid="order-payment-select"
            />
          </div>
          {order.paymentStatus !== 'PAID' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={saving}
              block={isMobile}
              onClick={() => update({ paymentStatus: 'PAID', status: 'PAID' })}
              data-testid="mark-paid-btn"
            >
              Als bezahlt markieren
            </Button>
          )}
        </Space>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card title="Positionen" style={{ marginBottom: 16 }}>
            <Table
              rowKey="id"
              dataSource={order.items}
              columns={itemColumns}
              pagination={false}
              size="small"
            />
            <Divider />
            <Row justify="space-between"><span>Zwischensumme</span><span>{fmt(order.subtotalCents)}</span></Row>
            <Row justify="space-between"><span>Versand</span><span>{fmt(order.shippingCents)}</span></Row>
            {order.discountCode && (
              <Row justify="space-between" style={{ color: '#b8964a' }} data-testid="order-discount-row">
                <span>Rabatt ({order.discountCode})</span>
                <span>− {fmt(order.discountCents || 0)}</span>
              </Row>
            )}
            <Row justify="space-between" style={{ fontWeight: 600, fontSize: 16, marginTop: 8 }}>
              <span>Gesamt</span><span>{fmt(order.totalCents)}</span>
            </Row>
          </Card>

          <Card title="Notizen">
            {order.customerNote && (
              <div
                style={{
                  background: 'rgba(184, 150, 74, 0.08)',
                  border: '1px solid #b8964a',
                  borderRadius: 4,
                  padding: '8px 12px',
                  marginBottom: 12,
                  whiteSpace: 'pre-wrap',
                }}
                data-testid="admin-order-customer-note"
              >
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Kundennachricht
                </Text>
                {order.customerNote}
              </div>
            )}
            <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              Interne Notizen
            </Text>
            <TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => update({ notes })}
              >
                Notizen speichern
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Kunde" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">
                {order.customerFirstName} {order.customerLastName}
              </Descriptions.Item>
              <Descriptions.Item label="E-Mail">
                <a href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a>
              </Descriptions.Item>
              {order.customerPhone && (
                <Descriptions.Item label="Telefon">
                  <a href={`tel:${order.customerPhone}`}>{order.customerPhone}</a>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Datum">
                {new Date(order.createdAt).toLocaleString('de-DE')}
              </Descriptions.Item>
              <Descriptions.Item label="Zahlung">
                {order.paymentMethod || 'SEPA'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {billing && (
            <Card title="Rechnungsadresse" style={{ marginBottom: 16 }} size="small">
              <div>{billing.firstName} {billing.lastName}</div>
              <div>{billing.street}</div>
              {billing.street2 && <div>{billing.street2}</div>}
              <div>{billing.postalCode} {billing.city}</div>
              <div>{billing.countryName || billing.countryCode}</div>
            </Card>
          )}

          {shipping && (
            <Card title="Lieferadresse" size="small">
              <div>{shipping.firstName} {shipping.lastName}</div>
              <div>{shipping.street}</div>
              {shipping.street2 && <div>{shipping.street2}</div>}
              <div>{shipping.postalCode} {shipping.city}</div>
              <div>{shipping.countryName || shipping.countryCode}</div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default OrderDetailPage;
